import * as React from 'react';
import { LoginSignupButtons, ProfileDropdown } from '@cx-web/components/composites';
import LoggedInMiniMyOC, { type MiniMyOCItem } from '@cx-web/components/composites/MiniMyOC';
import { type ILabelActions } from '@cx-web/feature-i18n';

import { type Repub } from '@cx-modules/constants';

import styles from './MiniMyOC.module.css';

// Manage-subscription portal (ProsperStack) fallback — mirrors ProfileDropdown's FALLBACK_PROSPERSTACK_URL.
// The host glue feeds the real URL from config (ENV.PROSPERSTACK_URL); this literal only guards the link
// from silently vanishing if that value is ever absent.
const FALLBACK_PROSPERSTACK_URL = 'https://app.prosperstack.com/p/portal/acct_C26RDWQxovsqswzDoFxz6get';

interface IProps {
  isLoggedIn: boolean;
  labelActions: ILabelActions;
  onLogout?: () => void;
  // Open the auth modal (NGF-6371): the auth-modal store opener bound to login/signup by the parent.
  onSignIn?: () => void;
  onSignUp?: () => void;
  // Manage-subscription portal (ProsperStack). Sourced from config (ENV.PROSPERSTACK_URL) by the glue,
  // not hardcoded — the shared presentational component is URL-agnostic and each app feeds the href.
  prosperStackUrl?: string;
  repub?: Repub;
  showProfileLink?: boolean;
  siteConfig?: { subscriptions?: { enabled?: boolean } };
  styles?: string;
  type: 'desktop' | 'burgerTop' | 'pageButton';
}

class MiniMyOC extends React.Component<IProps> {
  shouldComponentUpdate(nextProps: IProps): boolean {
    return (
      nextProps.type !== this.props.type ||
      nextProps.isLoggedIn !== this.props.isLoggedIn ||
      // labelActions resolves every rendered label (menu items + logout); track it so a changed
      // reference (e.g. a language switch) can't leave stale labels behind this SCU.
      nextProps.labelActions !== this.props.labelActions ||
      nextProps.repub !== this.props.repub ||
      // Callbacks forwarded to the UK components (ProfileDropdown/LoginSignupButtons):
      // tracked so a changed handler reference can't go stale behind this SCU.
      nextProps.onLogout !== this.props.onLogout ||
      nextProps.onSignIn !== this.props.onSignIn ||
      nextProps.onSignUp !== this.props.onSignUp ||
      nextProps.prosperStackUrl !== this.props.prosperStackUrl ||
      nextProps.showProfileLink !== this.props.showProfileLink ||
      nextProps.siteConfig?.subscriptions?.enabled !== this.props.siteConfig?.subscriptions?.enabled
    );
  }

  // Build the resolved menu entries the shared presentational LoggedInMiniMyOC expects: labels via the
  // Remix LabelService, relative hrefs, profile gated by showProfileLink, subscriptions by siteConfig.
  // Behaviour identical to the previous label-aware shared component.
  private buildItems = (): MiniMyOCItem[] => {
    const { labelActions, prosperStackUrl, showProfileLink, siteConfig } = this.props;
    return [
      ...(showProfileLink
        ? [{ key: 'profile', name: labelActions.get('PROFILE'), href: 'myoddschecker/profile' }]
        : []),
      { key: 'settings', name: labelActions.get('SETTINGS'), href: 'myoddschecker/settings' },
      // Subscriptions links out to the ProsperStack portal whenever subscriptions are enabled. The URL is
      // fed from config (ENV.PROSPERSTACK_URL); fall back to the literal so the link can't silently vanish
      // if that value is ever missing — matching ProfileDropdown (gate on `enabled` only, not the URL).
      ...(siteConfig?.subscriptions?.enabled
        ? [
            {
              key: 'subscriptions',
              name: labelActions.get('SUBSCRIPTIONS'),
              href: prosperStackUrl || FALLBACK_PROSPERSTACK_URL,
              external: true,
            },
          ]
        : []),
    ];
  };

  private renderLegacyLoggedIn = (type: IProps['type']): React.JSX.Element => {
    const { labelActions, onLogout } = this.props;
    return (
      <LoggedInMiniMyOC
        items={this.buildItems()}
        logoutLabel={labelActions.get('LOG_OUT')}
        onLogout={onLogout}
        profileHref="myoddschecker/profile"
        title={labelActions.get('MY_ODDSCHECKER')}
        type={type}
      />
    );
  };

  // Desktop renders the oc-uk-style header components. Props are passed explicitly (not the full IProps
  // superset) so each composite only receives what it declares.
  private renderDesktop = (): React.JSX.Element | null => {
    const { isLoggedIn, labelActions, onLogout, onSignIn, onSignUp, prosperStackUrl, repub, siteConfig } = this.props;

    return isLoggedIn ? (
      <ProfileDropdown
        labelActions={labelActions}
        onLogout={onLogout}
        prosperStackUrl={prosperStackUrl}
        repub={repub}
        siteConfig={siteConfig}
      />
    ) : (
      <LoginSignupButtons labelActions={labelActions} onSignIn={onSignIn} onSignUp={onSignUp} />
    );
  };

  private renderBurgerTop = (): React.JSX.Element | null => {
    const { isLoggedIn, labelActions, onSignIn } = this.props;

    if (isLoggedIn) {
      return this.renderLegacyLoggedIn('burgerTop');
    }

    return (
      <button
        className={[styles.miniOCButton, styles.login].join(' ')}
        id="loginLabel"
        onClick={onSignIn}
        type="button"
      >
        {labelActions.get('SIGN_IN')}
      </button>
    );
  };

  private renderPageButtonItem = (): React.JSX.Element => {
    const { isLoggedIn, labelActions, onSignUp } = this.props;

    if (isLoggedIn) {
      return this.renderLegacyLoggedIn('pageButton');
    }

    return (
      <div className={styles.loginButton}>
        <button onClick={onSignUp} type="button">
          {labelActions.get('SIGN_UP')}
        </button>
      </div>
    );
  };

  public render = (): React.JSX.Element | null => {
    switch (this.props.type) {
      case 'desktop':
        return this.renderDesktop();
      case 'burgerTop':
        return this.renderBurgerTop();
      case 'pageButton':
        return this.renderPageButtonItem();
      default:
        return null;
    }
  };
}

export default MiniMyOC;
